import { useMemo, useCallback, useRef, DependencyList } from "react";
import type { Ref } from "react";
import {
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
} from "@tanstack/react-table";
import { useClientsQuery } from "@api/workspace.api";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { WorkspaceSortValue } from "@common/types/views/workspaceViews";
import { Pagination } from "@design-system/Pagination/Pagination";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { ClientView } from "@common/types/views/clientViews";
import { View } from "@common/types/views/views";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { pluralize } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { StandardModal } from "@design-system/Modal/Modal";
import {
    ClientsFilters,
    ClientsQueryParams,
} from "@common/schemas/client.schema";
import { HStack } from "@design-system/HStack/HStack";
import { clientViewConfig, ClientDetailsRow } from "./views/clientViewConfig";
import styles from "./Clients.module.scss";
import { transformWorkspaceToClientRow } from "./helpers/transformWorkspaceToClientRow";
import { useClientBulkSelection } from "./ClientBulkSelection/useClientBulkSelection";
import { useClientBulkCommandActions } from "./useClientBulkCommandActions";
import { NoteAction } from "./ClientActionsBar/NoteAction/NoteAction";
import { ServicesAction } from "./ClientActionsBar/ServicesAction/ServicesAction";
import { ClientsEmptyView } from "./ClientsEmtpyView";
import { useOpenClientWorkspace } from "./useOpenClientWorkspace";

interface ClientsViewProps {
    filters?: ClientsFilters;
    view: ClientView;
    updateFilters?: (filters: ClientsFilters) => void;
    resetFilters?: () => void;
    hasActiveFilters?: boolean;
    onViewChange?: (view: ClientView) => void;
    page: number;
    sort: WorkspaceSortValue;
    onPageChange: (page: number) => void;
    onSortChange: (sort: WorkspaceSortValue) => void;
    expanded?: ExpandedState;
    onExpandedChange?: OnChangeFn<ExpandedState>;
    recomputeTopOffsetOn?: DependencyList;
}

const PAGE_SIZE = 20;

export const ClientsView = ({
    filters,
    view,
    updateFilters,
    resetFilters,
    hasActiveFilters,
    onViewChange,
    page,
    sort,
    onPageChange,
    onSortChange,
    expanded,
    onExpandedChange,
    recomputeTopOffsetOn,
    ref,
}: ClientsViewProps & { ref?: Ref<ViewTableHandle> }) => {
    const activeOrganization = useActiveOrganization();
    const bulkSelection = useClientBulkSelection();
    const footerRef = useRef<HTMLDivElement>(null);
    const {
        showMemo,
        closeMemo,
        showServices,
        closeServices,
        handleOpenBulkActions,
    } = useClientBulkCommandActions();

    const handleViewChange = useCallback(
        (updatedView: View) => {
            onViewChange?.(updatedView as ClientView);
        },
        [onViewChange],
    );

    const params: ClientsQueryParams = useMemo(
        () => ({
            filters,
            limit: PAGE_SIZE,
            page: page - 1, // Backend expects zero-based page
            sort,
            organizationId: activeOrganization?.id ?? "",
        }),
        [page, sort, filters, activeOrganization?.id],
    );

    const { data: clientsResponse } = useClientsQuery(params);
    const totalPages = clientsResponse?.pageCount ?? 1;

    const rows: ClientDetailsRow[] | null = useMemo(() => {
        if (!clientsResponse) {
            return null;
        }
        return clientsResponse.clients.map(transformWorkspaceToClientRow);
    }, [clientsResponse]);

    const enableRowSelection = useMemo(() => {
        if (!view.options?.bulkActions) {
            return false;
        }
        // Allow selection for both workspace and entity rows
        return () => true;
    }, [view.options?.bulkActions]);

    const rowSelection = useMemo(() => {
        if (!bulkSelection) {
            return {};
        }

        const selection: RowSelectionState = {};

        bulkSelection.selected.forEach((client) => {
            selection[client.id] = true;
        });

        return selection;
    }, [bulkSelection]);

    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            if (!rows) {
                return;
            }

            let nextSelection: RowSelectionState;
            if (typeof updater === "function") {
                nextSelection = updater(rowSelection);
            } else {
                nextSelection = updater;
            }

            const selectedIds = Object.keys(nextSelection).filter(
                (id) => nextSelection[id],
            );

            // Build a map of all selectable items (workspaces and entities)
            const itemsById = new Map<string, ClientDetailsRow>();
            rows.forEach((row) => {
                itemsById.set(String(row.id), row);
                if (row.entities) {
                    row.entities.forEach((entity) => {
                        // Create a ClientDetailsRow for the entity by copying workspace-level fields
                        const entityRow: ClientDetailsRow = {
                            ...row,
                            ...entity,
                            id: String(entity.id),
                            name: entity.name,
                            type: "entity" as const,
                            entities: [],
                        };
                        itemsById.set(String(entity.id), entityRow);
                    });
                }
            });

            const selectedClients = selectedIds
                .map((id) => itemsById.get(id))
                .filter((item): item is ClientDetailsRow => item !== undefined);

            if (selectedIds.length === 0) {
                bulkSelection.clearSelection();
            } else {
                bulkSelection.clearSelection();
                bulkSelection.select(selectedClients);
            }
        },
        [bulkSelection, rows, rowSelection],
    );

    const openClientWorkspace = useOpenClientWorkspace();
    const handleRowClick = useCallback(
        (row: ClientDetailsRow | undefined) => {
            const workspaceId =
                row?.type === "entity" ? row.workspaceId : row?.id;
            if (workspaceId) {
                openClientWorkspace(workspaceId);
            }
        },
        [openClientWorkspace],
    );

    const pageChangeWithBulkActionReset = useCallback(
        (newPage: number) => {
            if (bulkSelection.selectedCount) {
                bulkSelection.clearSelection();
            }
            onPageChange(newPage);
        },
        [bulkSelection, onPageChange],
    );

    return (
        <div className={styles.clientsViewContainer}>
            <ViewTable
                ref={ref}
                data={rows}
                view={view}
                config={clientViewConfig}
                sort={sort}
                onSortChange={onSortChange}
                className={styles.clientsView}
                enableKeyboardShortcuts
                onViewChange={handleViewChange}
                enableColumnResizing
                rowSelection={rowSelection}
                onRowSelectionChange={handleRowSelectionChange}
                enableRowSelection={enableRowSelection}
                expanded={expanded}
                onExpandedChange={onExpandedChange}
                onRowClick={handleRowClick}
                isParentSelectable
                emptyView={() => (
                    <ClientsEmptyView
                        total={clientsResponse?.total}
                        hasActiveFilters={hasActiveFilters ?? false}
                        resetFilters={resetFilters}
                        updateFilters={updateFilters}
                    />
                )}
                fixedTopOffsetPx
                recomputeTopOffsetOn={recomputeTopOffsetOn}
                footer={
                    clientsResponse?.total ? (
                        <CommandIsland
                            sticky
                            ref={footerRef}
                            className={
                                commandIslandStyles.bulkActionsStickyFooter
                            }
                        >
                            <HStack gap={8}>
                                {totalPages > 1 ? (
                                    <Pagination
                                        size="md"
                                        pageCount={totalPages}
                                        currentPage={page}
                                        onPageChange={
                                            pageChangeWithBulkActionReset
                                        }
                                    />
                                ) : (
                                    <CommandIslandTotal>
                                        {pluralize(
                                            "Client",
                                            clientsResponse.total,
                                        )}
                                        : {clientsResponse.total}
                                    </CommandIslandTotal>
                                )}

                                {bulkSelection.selectedCount > 0 && (
                                    <Tooltip
                                        key={bulkSelection.selectedCount}
                                        content={`Deselect ${bulkSelection.selectedCount}`}
                                    >
                                        <Button
                                            variant="primary"
                                            size="md"
                                            onClick={
                                                bulkSelection.clearSelection
                                            }
                                        >
                                            {bulkSelection.selectedCount}{" "}
                                            selected
                                        </Button>
                                    </Tooltip>
                                )}
                            </HStack>

                            {bulkSelection.selectedCount > 0 && (
                                <Tooltip content="Open bulk actions">
                                    <Button
                                        variant="primary"
                                        size="md"
                                        onClick={handleOpenBulkActions}
                                        rightIcon={
                                            <span data-slot-box="">
                                                <Shortcut keys="mod+k" />
                                            </span>
                                        }
                                    >
                                        Actions
                                    </Button>
                                </Tooltip>
                            )}
                        </CommandIsland>
                    ) : undefined
                }
            />

            <StandardModal show={showMemo} onClose={closeMemo} size="sm">
                <NoteAction
                    inputPlaceholder="Add memo..."
                    onSubmitCallback={closeMemo}
                />
            </StandardModal>

            <StandardModal
                show={showServices}
                onClose={closeServices}
                size="sm"
            >
                <ServicesAction onSubmitCallback={closeServices} />
            </StandardModal>
        </div>
    );
};
